/**
 * Atelier Royal: animations.js (ES module, every page; import map name "@theme/animations").
 *
 * [data-reveal]         fade + rise 14px when the element enters the viewport
 * [data-reveal="blur"]  blur-to-sharp (portraits); waits for the image to load
 * [data-reveal="zoom"]  one slow 26s zoom (≤ 1.06×) of the inner image
 * Optional style="--reveal-delay: 120ms" to stagger siblings.
 *
 * Everything is disabled under prefers-reduced-motion, when Theme settings > Motion is off
 * (no html.ar-motion), or without IntersectionObserver: elements are shown immediately.
 * CSS lives in base.css §7. Never put data-reveal on the hero / LCP element.
 */

window.ARMotionReady = true; // tells the inline fail-safe in theme.liquid that this module loaded

const root = document.documentElement;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function motionEnabled() {
  return root.classList.contains('ar-motion') && !reducedMotion.matches && 'IntersectionObserver' in window;
}

function whenLoaded(el, callback) {
  const img = el instanceof HTMLImageElement ? el : el.querySelector('img');
  if (!img || img.complete) {
    callback();
    return;
  }
  img.addEventListener('load', callback, { once: true });
  img.addEventListener('error', callback, { once: true });
}

function reveal(el) {
  if (el.dataset.reveal === 'blur') whenLoaded(el, () => el.classList.add('is-revealed'));
  else el.classList.add('is-revealed');
}

let observer = null;

function getObserver() {
  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          reveal(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
  }
  return observer;
}

/** Observes every not-yet-revealed [data-reveal] element inside `scope`. */
export function observeReveals(scope = document) {
  const elements = scope.querySelectorAll('[data-reveal]:not(.is-revealed)');
  if (!motionEnabled()) {
    elements.forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  const io = getObserver();
  elements.forEach((el) => io.observe(el));
}

/**
 * Blur-to-sharp reveal for an element that is already on screen (e.g. a freshly generated
 * preview in the creator). Resets the state, then reveals once the image has loaded.
 */
export function blurReveal(el) {
  if (!el) return;
  el.dataset.reveal = 'blur';
  if (!motionEnabled()) {
    el.classList.add('is-revealed');
    return;
  }
  el.classList.remove('is-revealed');
  void el.offsetWidth; // restart the transition
  whenLoaded(el, () => requestAnimationFrame(() => el.classList.add('is-revealed')));
}

observeReveals();

reducedMotion.addEventListener?.('change', () => {
  if (reducedMotion.matches) document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
});

document.addEventListener('shopify:section:load', (event) => observeReveals(event.target));
document.addEventListener('ar:reveal:refresh', (event) => observeReveals(event.target instanceof Element ? event.target : document));
